import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  HeartHandshake, Shield, Home, Users, Sprout, Compass,
  Briefcase, GraduationCap, CreditCard, Brain, Stethoscope,
} from "lucide-react";
import { getAvailability } from "@/lib/api";

export const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 32 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

export const RibbonMarquee = () => (
  <div className="bg-muted border-y border-border py-6" data-testid="marquee">
    <Marquee speed={30} gradient={false} autoFill>
      {["STABILITY", "DIGNITY", "INDEPENDENCE", "HEALING", "COMMUNITY", "HOPE"].map((w, i) => (
        <span key={i} className="font-serif italic text-3xl md:text-4xl text-secondary/70 mx-14 flex items-center gap-14">
          {w}
          <span className="text-primary text-xl not-italic">✦</span>
        </span>
      ))}
    </Marquee>
  </div>
);

const chapters = [
  {
    n: "01",
    title: "Our Vision",
    body: "A community where every individual—regardless of their past challenges or current circumstances—has access to safe, supportive, and structured housing opportunities that promote healing, stability, and independence. We envision a future where veterans, individuals in recovery, and adults transitioning from homelessness are empowered to rebuild their lives with dignity, purpose, and hope.",
  },
  {
    n: "02",
    title: "Our Mission",
    body: "To provide safe, structured, and supportive shared housing opportunities that empower veterans, individuals in recovery, and adults transitioning from homelessness to rebuild stability, restore dignity, and move toward independent living—through compassionate support, community partnerships, and pathways to long-term self-sufficiency.",
  },
];

export const Manifesto = () => (
  <section id="mission" data-testid="mission-section" className="bg-background py-28 lg:py-40">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <Reveal>
        <p className="text-xs font-sans uppercase tracking-[0.3em] font-semibold text-primary mb-4">
          What guides us
        </p>
        <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-foreground max-w-3xl leading-[0.95]">
          Built on compassion, structure, and accountability.
        </h2>
      </Reveal>

      <div className="mt-20 space-y-20">
        {chapters.map((c, i) => (
          <Reveal key={c.n} delay={i * 0.05}>
            <div className="grid lg:grid-cols-12 gap-6 lg:gap-12 items-start border-t border-border pt-10">
              <div className="lg:col-span-3">
                <span className="font-serif text-6xl lg:text-7xl text-primary leading-none">{c.n}</span>
                <h3 className="font-serif text-3xl text-foreground mt-3">{c.title}</h3>
              </div>
              <p className="lg:col-span-9 font-sans text-lg lg:text-xl text-foreground/75 leading-relaxed max-w-3xl">
                {c.body}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const serve = [
  {
    icon: Shield,
    title: "Veterans",
    text: "Honoring service with stable housing and a supportive community that understands the journey home.",
    img: "https://images.unsplash.com/photo-1698671114507-6ae8567cb895?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwzfHxob3BlZnVsJTIwcG9ydHJhaXQlMjB2ZXRlcmFuJTIwY29tbXVuaXR5JTIwc3VwcG9ydHxlbnwwfHx8fDE3ODUxNzkyMTV8MA&ixlib=rb-4.1.0&q=85",
    span: "lg:col-span-7 lg:row-span-2",
    tall: true,
  },
  {
    icon: HeartHandshake,
    title: "Individuals in Recovery",
    text: "A structured, sober environment with healthy living practices and supportive partnerships.",
    img: "https://images.unsplash.com/photo-1573497491765-dccce02b29df?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
    span: "lg:col-span-5",
  },
  {
    icon: Home,
    title: "Transitioning from Homelessness",
    text: "Pathways from instability to independence, with dignity at every step.",
    img: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
    span: "lg:col-span-5",
  },
];

export const WhoWeServe = () => (
  <section id="serve" data-testid="serve-section" className="bg-card py-28 lg:py-40">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <Reveal>
        <p className="text-xs font-sans uppercase tracking-[0.3em] font-semibold text-primary mb-4">
          Who we serve
        </p>
        <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-foreground max-w-3xl leading-[0.95]">
          Every resident is treated with respect and compassion.
        </h2>
      </Reveal>

      <div className="mt-16 grid lg:grid-cols-12 gap-6 lg:gap-8 auto-rows-fr">
        {serve.map((s, i) => {
          const Icon = s.icon;
          return (
            <Reveal key={s.title} delay={i * 0.08} className={s.span}>
              <div className="h-full rounded-2xl bg-background border border-border p-8 flex flex-col justify-between overflow-hidden group">
                {s.img && (
                  <div className="mb-6 overflow-hidden rounded-xl aspect-[16/10]">
                    <img
                      src={s.img}
                      alt={s.title}
                      className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                  </div>
                )}
                <div>
                  <Icon className="text-primary mb-5" size={30} strokeWidth={1.5} />
                  <h3 className="font-serif text-3xl text-foreground">{s.title}</h3>
                  <p className="mt-3 font-sans text-foreground/70 leading-relaxed">{s.text}</p>
                </div>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

const steps = [
  { n: "01", icon: Compass, title: "Reach Out", text: "Submit an intake application. Share your situation and needs in confidence." },
  { n: "02", icon: Users, title: "Personal Review", text: "Our team reviews your application and connects with you to understand your path." },
  { n: "03", icon: Home, title: "Move In", text: "Join a structured, supportive shared home with community and accountability." },
  { n: "04", icon: Sprout, title: "Grow & Transition", text: "Access resources and partnerships that build toward long-term independence." },
];

export const HowItWorks = () => (
  <section id="process" data-testid="process-section" className="bg-secondary py-28 lg:py-40">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <Reveal>
        <p className="text-xs font-sans uppercase tracking-[0.3em] font-semibold text-primary mb-4">
          The pathway
        </p>
        <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-secondary-foreground max-w-3xl leading-[0.95]">
          A clear path from where you are to where you&apos;re going.
        </h2>
      </Reveal>

      <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-8">
        {steps.map((s, i) => {
          const Icon = s.icon;
          return (
            <Reveal key={s.n} delay={i * 0.08}>
              <div className="border-t border-secondary-foreground/25 pt-6">
                <span className="font-serif text-5xl text-primary">{s.n}</span>
                <Icon className="text-secondary-foreground/80 mt-6 mb-4" size={26} strokeWidth={1.5} />
                <h3 className="font-serif text-2xl text-secondary-foreground">{s.title}</h3>
                <p className="mt-2 font-sans text-secondary-foreground/70 leading-relaxed text-sm">{s.text}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

const services = [
  { icon: Briefcase, title: "Case Management", text: "Dedicated coordinators connecting residents to resources and a personalized plan." },
  { icon: GraduationCap, title: "DUI Intervention & Education", text: "Structured intervention and education programs supporting accountability and change." },
  { icon: CreditCard, title: "Credit Repair", text: "Guidance to rebuild financial health and prepare for independent living." },
  { icon: Brain, title: "Mental Health Evaluation", text: "Compassionate assessments connecting residents to the right support." },
  { icon: Stethoscope, title: "Primary Care Referrals", text: "Referrals to trusted partners for ongoing physical health and wellness." },
];

export const ConnectedServices = () => (
  <section id="services" data-testid="services-section" className="bg-background py-28 lg:py-40">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <Reveal>
        <p className="text-xs font-sans uppercase tracking-[0.3em] font-semibold text-primary mb-4">
          Connected services
        </p>
        <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-foreground max-w-3xl leading-[0.95]">
          Support that reaches beyond a roof over your head.
        </h2>
        <p className="mt-6 font-sans text-lg text-foreground/70 max-w-2xl leading-relaxed">
          Through community partnerships, every resident is connected to wraparound resources
          that build a foundation for long-term success.
        </p>
      </Reveal>

      <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {services.map((s, i) => {
          const Icon = s.icon;
          return (
            <Reveal key={s.title} delay={i * 0.06}>
              <div className="h-full rounded-2xl bg-card border border-border p-8 group">
                <div className="w-12 h-12 rounded-full bg-primary/15 flex items-center justify-center mb-5 transition-colors duration-300 group-hover:bg-primary">
                  <Icon className="text-primary transition-colors duration-300 group-hover:text-primary-foreground" size={22} strokeWidth={1.6} />
                </div>
                <h3 className="font-serif text-2xl text-foreground">{s.title}</h3>
                <p className="mt-2 font-sans text-foreground/70 leading-relaxed text-sm">{s.text}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

export const AvailabilityBand = ({ onApply }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    getAvailability().then(setData).catch(() => {});
  }, []);

  const available = data?.available ?? 0;
  const isOpen = available > 0;

  return (
    <section data-testid="availability-band" className="bg-primary py-16">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
        <Reveal className="flex items-center gap-6">
          <span className="relative flex h-3 w-3">
            <span className={`absolute inline-flex h-full w-full rounded-full opacity-75 ${isOpen ? "bg-secondary animate-ping" : "bg-destructive"}`} />
            <span className={`relative inline-flex rounded-full h-3 w-3 ${isOpen ? "bg-secondary" : "bg-destructive"}`} />
          </span>
          <div>
            <p className="font-serif text-3xl sm:text-4xl text-primary-foreground leading-none" data-testid="availability-headline">
              {data
                ? isOpen
                  ? `${available} ${available === 1 ? "spot" : "spots"} available now`
                  : "Currently full — join the waitlist"
                : "Checking availability…"}
            </p>
            <p className="mt-2 font-sans text-primary-foreground/70 text-sm">
              {data && data.waitlist > 0
                ? `${data.waitlist} application${data.waitlist === 1 ? "" : "s"} in review. Apply early to secure your place.`
                : "Warm, structured shared housing — apply today to begin your journey."}
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <button
            data-testid="availability-apply-btn"
            onClick={onApply}
            className="rounded-full bg-foreground text-background hover:bg-background hover:text-foreground transition-colors duration-300 px-8 h-12 text-base font-medium whitespace-nowrap"
          >
            {isOpen ? "Apply for a Spot" : "Join the Waitlist"}
          </button>
        </Reveal>
      </div>
    </section>
  );
};

export const Footer = () => (
  <footer data-testid="footer" className="bg-foreground text-background py-16">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row justify-between gap-8">
      <div>
        <p className="font-serif text-3xl">Fresh Start Living Services</p>
        <p className="text-background/60 text-sm mt-2 max-w-md">
          Transitional Living Services — rebuilding stability, restoring dignity, moving toward independence.
        </p>
      </div>
      <div className="text-sm text-background/70 space-y-1">
        <p>info@freshstartservices.net</p>
        <p>708-393-1243</p>
        <a href="/admin" data-testid="footer-admin-link" className="inline-block mt-4 text-primary hover:underline">
          Staff Login
        </a>
      </div>
    </div>
    <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-10 pt-6 border-t border-background/15 text-xs text-background/40">
      © {new Date().getFullYear()} Fresh Start Living Services. All rights reserved.
    </div>
  </footer>
);
